<template>
  <div class="sellers-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-user-tie"></i>
        Vendedores
      </h1>
      <p class="page-subtitle">Gerencie os vendedores e suas configurações</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="selectedTab = 1"
        >
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          class="tab-button" 
          :class="{ 
            active: selectedTab === 2,
            disabled: !canCreateEditSellers 
          }" 
          @click="canCreateEditSellers ? handleCadastroTabClick() : showPermissionError()"
          :disabled="!canCreateEditSellers"
          :title="!canCreateEditSellers ? 'Você não tem permissão para criar/editar vendedores' : ''"
        >
          <i class="fa-solid fa-plus tab-plus-icon"></i>
          {{ seller.id ? 'EDITAR' : 'CADASTRO' }}
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <div class="table-card">
        <!-- Cabeçalho da tabela -->
        <div class="table-header">
          <div class="search-container">
            <div class="search-input-group">
              <i class="fa-solid fa-search search-icon"></i>
              <input 
                type="text" 
                v-model="quickFilter" 
                class="search-input" 
                placeholder="Pesquisar vendedores..." 
                autocomplete="off"
              />
            </div>
          </div>
          <span v-if="totalRows > 0" class="table-count">({{ totalRows }} vendedor(es) encontrado(s))</span>
          <div v-if="totalPages > 1" class="pagination-info">
            <span>Página {{ pageIndex }} de {{ totalPages }}</span>
            <div class="pagination-controls">
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex - 1)"
                :disabled="pageIndex <= 1"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex + 1)"
                :disabled="pageIndex >= totalPages"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <div class="table-zoom-controls" title="Zoom da tabela">
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom <= 5"
              @click="decreaseTableZoom"
              title="Diminuir zoom"
            >
              <i class="fa-solid fa-magnifying-glass-minus"></i>
            </button>
            <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
              {{ tableZoom }}%
            </span>
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom >= 100"
              @click="increaseTableZoom"
              title="Aumentar zoom"
            >
              <i class="fa-solid fa-magnifying-glass-plus"></i>
            </button>
          </div>
          <div class="card-actions">
            <button
              type="button"
              class="btn-action btn-print"
              @click="printReport()"
              title="Imprimir relatório"
            >
              <i class="fa-solid fa-print"></i>
              Imprimir
            </button>
            <button
              type="button"
              class="btn-action btn-pdf"
              @click="exportToPDF()"
              title="Exportar para PDF"
            >
              <i class="fa-solid fa-file-pdf"></i>
              PDF
            </button>
            <button
              type="button"
              class="btn-action btn-excel"
              @click="exportToExcel()"
              title="Exportar para Excel"
            >
              <i class="fa-solid fa-file-excel"></i>
              Excel
            </button>
          </div>
          <button type="button" class="btn-primary" @click="createSeller()" v-if="canCreateEditSellers">
            <i class="fa-solid fa-plus"></i>
            Adicionar Vendedor
          </button>
        </div>

        <!-- Mensagem informativa para usuários com permissões limitadas -->
        <div v-if="!isAdmin" class="permission-info">
          <div class="info-card">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-content">
              <span v-if="isBanca">
                <strong>Modo BANCA:</strong> Você pode visualizar apenas os vendedores de suas regiões e gerenciar vendedores (criar/editar/excluir).
                <span v-if="currentSelectedRegion"> Exibindo vendedores da região: <strong>{{ currentSelectedRegion.name }}</strong></span>
              </span>
              <span v-else-if="isGerente">
                <strong>Modo GERENTE:</strong> Você pode visualizar apenas os vendedores de suas regiões e gerenciar vendedores (criar/editar/excluir).
                <span v-if="currentSelectedRegion"> Exibindo vendedores da região: <strong>{{ currentSelectedRegion.name }}</strong></span>
              </span>
              <span v-else-if="isCobrador">
                <strong>Modo COBRADOR:</strong> Você visualiza e gerencia apenas os vendedores vinculados a você (criar/editar). Não pode excluir vendedores.
                <span v-if="currentSelectedRegion"> Exibindo vendedores da região: <strong>{{ currentSelectedRegion.name }}</strong></span>
              </span>
            </div>
          </div>
        </div>

        <!-- Mensagem informativa para admin -->
        <div v-if="isAdmin" class="permission-info">
          <div class="info-card">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-content">
              <span v-if="currentSelectedBanca && currentSelectedRegion">
                <strong>Filtros Ativos:</strong> Exibindo vendedores da banca <strong>{{ currentSelectedBanca.name }}</strong>
                e região <strong>{{ currentSelectedRegion.name }}</strong>.
              </span>
              <span v-else-if="currentSelectedBanca && !currentSelectedRegion">
                <strong>Filtro por Banca:</strong> Exibindo todos os vendedores da banca <strong>{{ currentSelectedBanca.name }}</strong>.
                Para filtrar por região específica, selecione uma região no filtro.
              </span>
              <span v-else-if="!currentSelectedBanca && currentSelectedRegion">
                <strong>Filtro por Região:</strong> Exibindo vendedores da região <strong>{{ currentSelectedRegion.name }}</strong>.
                Para filtrar por banca específica, selecione uma banca no filtro.
              </span>
              <span v-else>
                <strong>Visualização Global:</strong> Exibindo todos os vendedores do sistema.
                Para filtrar, selecione uma banca e/ou região nos filtros localizados no topo da página.
              </span>
            </div>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-container" :style="tableZoomStyle">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Login</th>
                <th>Região</th>
                <th>Telefone</th>
                <th>Ponto</th>
                <th>Comissão</th>
                <th>Comissão Cobrador</th>
                <th>Tipo Comissão</th>
                <th>Limite Carteira</th>
                <th>Saldo Carteira</th>
                <th>Cobrador</th>
                <th>Modalidade</th>
                <th>Terminal</th>
                <th>Status</th>
                <th width="140">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="s in paginatedSellers" 
                :key="s.id"
                class="table-row"
              >
                <td>
                  <div class="seller-info">
                    <span class="seller-name">{{ s.name }}</span>
                  </div>
                </td>
                <td>{{ s.login || '-' }}</td>
                <td>{{ getRegionName(s.regionId) }}</td>
                <td>{{ s.phone || '-' }}</td>
                <td>{{ s.point || '-' }}</td>
                <td>
                  <span class="commission-badge">{{ formatCommission(s.comission) }}%</span>
                </td>
                <td>
                  <span v-if="s.payCollectorCommission === true" class="commission-badge">{{ formatCommission(s.collectorCommission) }}%</span>
                  <span v-else class="status-badge badge-danger">NÃO</span>
                </td>
                <td>{{ s.collectorCommissionType ? 'Bruto' : 'Líquido' }}</td>
                <td>
                  <span class="wallet-badge">R$ {{ formatCurrency(s.walletLimit) }}</span>
                </td>
                <td>
                  <span :class="['wallet-balance-badge', s.walletBalance >= 0 ? 'positive' : 'negative']">
                    R$ {{ formatCurrency(s.walletBalance) }}
                  </span>
                </td>
                <td>{{ getCollectorName(s.collectorId) }}</td>
                <td>{{ s.modality?.name || '-' }}</td>
                <td>{{ s.terminal?.key || '-' }}</td>
                <td>
                  <span :class="['status-badge', s.isActive ? 'badge-success' : 'badge-danger']">
                    {{ s.isActive ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button 
                      class="btn-action btn-edit" 
                      @click="editSeller(s)"
                      title="Editar"
                      v-if="canCreateEditSellers"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button 
                      v-if="canCreateEditSellers"
                      class="btn-action"
                      :class="s.isActive ? 'btn-deactivate' : 'btn-activate'"
                      @click="toggleSellerStatus(s.id, !s.isActive)"
                      :title="s.isActive ? 'Desativar vendedor' : 'Ativar vendedor'"
                    >
                      <i :class="s.isActive ? 'fa-solid fa-user-slash' : 'fa-solid fa-user-check'"></i>
                    </button>
                    <button 
                      class="btn-action btn-delete" 
                      @click="deleteSeller(s.id)"
                      title="Excluir"
                      v-if="canDeleteSellers"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredSellers.length === 0" class="no-data">
                <td colspan="15">
                  <div class="no-data-message">
                    <i class="fa-solid fa-user-tie"></i>
                    <span>Nenhum vendedor encontrado</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-footer">
          <div class="pagination-info">
            <span>Mostrando {{ startItem }}-{{ endItem }} de {{ totalRows }} resultados</span>
          </div>
          <div class="pagination-controls">
            <button 
              class="btn-pagination" 
              @click="changePage(1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex - 1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="page-info">{{ pageIndex }} / {{ totalPages }}</span>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex + 1)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(totalPages)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="form-card">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-user-tie"></i>
            {{ seller.id ? 'Editar Vendedor' : 'Novo Vendedor' }}
            <p class="form-subtitle">
              {{ seller.id ? 'Atualize as informações do vendedor' : 'Preencha as informações do novo vendedor' }}
            </p>
          </h3>
        </div>

        <div class="form-body">
          <div class="form-grid">
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user"></i>
                Nome *
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="seller.name"
                placeholder="Digite o nome do vendedor..."
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-phone"></i>
                Telefone
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="seller.phone"
                v-maska="'(##) #####-####'"
                placeholder="Digite o telefone..."
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user"></i>
                Login *
              </label>
              <div class="input-with-icon">
                <input 
                  class="form-input" 
                  type="text" 
                  v-model="seller.login"
                  placeholder="Digite o login..."
                  autocomplete="off"
                />
                <button 
                  type="button" 
                  class="btn-generate-icon" 
                  @click="generateLogin()"
                  title="Gerar código automaticamente"
                >
                  <i class="fa-solid fa-refresh"></i>
                </button>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-lock"></i>
                Senha {{ seller.id ? '' : '*' }}
              </label>
              <input 
                class="form-input" 
                type="password" 
                v-model="seller.password"
                :placeholder="seller.id ? 'Deixe em branco para manter a senha atual' : 'Digite a senha...'"
                autocomplete="new-password"
              />
              <small v-if="seller.id" class="field-hint">
                <i class="fa-solid fa-info-circle"></i>
                Deixe em branco para manter a senha atual
              </small>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-map-marker-alt"></i>
                Ponto
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="seller.point"
                placeholder="Digite o ponto de venda..."
              />
            </div>

            <!-- Endereço (opcional) -->
            <div class="input-group full-width address-section-title">
              <label class="input-label">
                <i class="fa-solid fa-location-dot"></i>
                Endereço
                <span class="optional-badge">opcional</span>
              </label>
              <small class="field-hint">
                <i class="fa-solid fa-info-circle"></i>
                Informe o CEP para preencher automaticamente os campos. Não é obrigatório cadastrar endereço.
              </small>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-map-pin"></i>
                CEP
                <i
                  v-if="loadingCep"
                  class="fa-solid fa-spinner fa-spin cep-loading-icon"
                  title="Buscando endereço..."
                ></i>
              </label>
              <input
                class="form-input"
                type="text"
                v-model="seller.cep"
                v-maska="'#####-###'"
                placeholder="00000-000"
                @input="onCepInput"
              />
              <small v-if="cepError" class="field-hint text-danger">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{ cepError }}
              </small>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-road"></i>
                Logradouro
              </label>
              <input
                class="form-input"
                type="text"
                v-model="seller.street"
                placeholder="Rua, avenida..."
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-hashtag"></i>
                Número
              </label>
              <input
                class="form-input"
                type="text"
                v-model="seller.addressNumber"
                placeholder="Nº"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-building"></i>
                Complemento
              </label>
              <input
                class="form-input"
                type="text"
                v-model="seller.complement"
                placeholder="Apto, sala, bloco..."
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-map"></i>
                Bairro
              </label>
              <input
                class="form-input"
                type="text"
                v-model="seller.neighborhood"
                placeholder="Bairro"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-city"></i>
                Cidade
              </label>
              <input
                class="form-input"
                type="text"
                v-model="seller.city"
                placeholder="Cidade"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-flag"></i>
                UF
              </label>
              <input
                class="form-input"
                type="text"
                v-model="seller.state"
                maxlength="2"
                placeholder="UF"
                @input="seller.state = (seller.state || '').toUpperCase()"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-percentage"></i>
                Comissão (%)
              </label>
              <input 
                class="form-input" 
                type="number" 
                step="0.01"
                v-model="seller.comission"
                placeholder="Digite a comissão..."
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-wallet"></i>
                Limite da Carteira *
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="seller.walletLimit"
                @input="formatWalletLimitInput"
                @blur="formatWalletLimitBlur"
                placeholder="Digite o limite da carteira..."
              />
              <small class="field-hint">
                <i class="fa-solid fa-info-circle"></i>
                Valor máximo que o vendedor pode apostar até que o limite seja atingido
              </small>
            </div>

            <div class="input-group" v-if="seller.id">
              <label class="input-label">
                <i class="fa-solid fa-coins"></i>
                Saldo Atual da Carteira (R$)
              </label>
              <input 
                class="form-input" 
                type="text" 
                :value="formatCurrency(seller.walletBalance || 0)"
                disabled
                readonly
              />
              <small class="field-hint">
                <i class="fa-solid fa-info-circle"></i>
                <span :class="seller.walletBalance >= 0 ? 'text-success' : 'text-danger'">
                  Saldo atual: {{ seller.walletBalance >= 0 ? 'Positivo' : 'Negativo' }}
                </span>
              </small>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-percentage"></i>
                Comissão do Cobrador (%)
              </label>
              <input 
                class="form-input" 
                type="number" 
                step="0.01"
                v-model="seller.collectorCommission"
                placeholder="Digite a comissão do cobrador..."
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-calculator"></i>
                Tipo de Comissão do Cobrador
              </label>
              <select 
                class="form-input" 
                v-model="seller.collectorCommissionType"
              >
                <option :value="true">Bruto (sobre valor total das vendas)</option>
                <option :value="false">Líquido (após deduzir comissão do vendedor)</option>
              </select>
              <small class="field-hint">
                <i class="fa-solid fa-info-circle"></i>
                <strong>Bruto:</strong> Comissão calculada sobre o valor total das vendas<br/>
                <strong>Líquido:</strong> Comissão calculada após deduzir a comissão do vendedor
              </small>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-check"></i>
                Cobrador
              </label>
              <select 
                class="form-input" 
                v-model="seller.collectorId"
                :disabled="isCobrador"
              >
                <option value="null">Selecione um cobrador</option>
                <option 
                  v-for="collector in filteredCollectors" 
                  :key="collector.id"
                  :value="collector.id"
                  :selected="seller.collectorId == collector.id"
                >
                  {{ collector.name }}
                </option>
              </select>
              <small v-if="isCobrador" class="field-hint">
                <i class="fa-solid fa-info-circle"></i>
                Vendedores criados por você ficam automaticamente vinculados ao seu usuário.
              </small>
              <small v-else-if="seller.regionId && filteredCollectors.length === 0" class="field-hint">
                <i class="fa-solid fa-exclamation-triangle"></i>
                Nenhum cobrador encontrado para esta região
              </small>
            </div>

            <!-- Região Ativa (similar ao ViewCollectors) -->
            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-map-location-dot"></i>
                Região *
              </label>
              <div class="region-info-container">
                <div v-if="currentSelectedRegion" class="region-info-display">
                  <div class="active-region">
                    <i class="fa-solid fa-check-circle"></i>
                    <span class="region-name">{{ currentSelectedRegion.name }}</span>
                    <span class="region-status">Região Ativa</span>
                  </div>
                  <small class="region-help">
                    <i class="fa-solid fa-info-circle"></i>
                    O vendedor será automaticamente associado à região ativa selecionada.
                  </small>
                </div>
                
                <div v-else class="no-region-warning">
                  <div class="warning-content">
                    <i class="fa-solid fa-exclamation-triangle"></i>
                    <div class="warning-text">
                      <strong>Nenhuma região ativa selecionada</strong>
                      <p>É necessário selecionar uma região ativa no filtro para criar um vendedor.</p>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    class="btn-select-region"
                    @click="showRegionSelectionMessage"
                  >
                    <i class="fa-solid fa-map-location-dot"></i>
                    Como selecionar uma região?
                  </button>
                </div>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-gamepad"></i>
                Modalidade
                <span v-if="!seller.id" class="required-asterisk"> *</span>
              </label>
              <select 
                class="form-input" 
                v-model="seller.regionModalityParameterId" 
                @change="selectModality()"
                :class="{ 'error-field': !seller.id && isModalityMissingForNewSeller }"
              >
                <option value="null">Selecione uma modalidade</option>
                <option 
                  v-for="m in activeModalities" 
                  :key="m.id"
                  :value="m.id"
                  :selected="seller.regionModalityParameterId == m.id"
                >
                  {{ m.name }}
                </option>
              </select>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-desktop"></i>
                Terminal
              </label>
              <input 
                class="form-input" 
                type="text"
                disabled
                :value="seller.terminal ? seller.terminal.key : 'Não definido'"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-link"></i>
                Origem da Descarga
                <span v-if="currentSelectedRegion && currentSelectedRegion.onlyDescarga" class="required-asterisk"> *</span>
              </label>
              <select 
                class="form-input" 
                v-model="seller.idRegionDescarga"
                :class="{ 'error-field': currentSelectedRegion && currentSelectedRegion.onlyDescarga && (!seller.idRegionDescarga || seller.idRegionDescarga === null) }"
              >
                <option :value="null">
                  {{ currentSelectedRegion && currentSelectedRegion.onlyDescarga ? 'Selecione uma origem da descarga (obrigatório)' : 'Selecione uma origem da descarga (opcional)' }}
                </option>
                <option 
                  v-for="region in availableDescargaRegions" 
                  :key="region.id"
                  :value="region.id"
                  :selected="seller.idRegionDescarga == region.id"
                >
                  {{ region.name }} {{ region.onlyDescarga ? '(Descarga)' : '(Normal)' }}
                </option>
              </select>
              <small class="field-hint">
                <i class="fa-solid fa-info-circle"></i>
                <span v-if="currentSelectedRegion && currentSelectedRegion.onlyDescarga" class="text-danger">
                  <strong>Obrigatório:</strong> Para regiões de descarga é necessário selecionar uma origem da descarga para o vendedor
                </span>
                <span v-else>
                  Selecione uma origem da descarga para vincular a este vendedor (opcional)
                </span>
              </small>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-percentage"></i>
                % de Retorno da Descarga
              </label>
              <input 
                class="form-input" 
                type="number" 
                step="0.01"
                v-model="seller.descargaReturnPercent"
                placeholder="Digite a % de retorno da descarga..."
              />
              <small class="field-hint">
                <i class="fa-solid fa-info-circle"></i>
                Percentual de retorno que o vendedor recebe das descargas (opcional)
              </small>
            </div>

            <div class="input-group input-group-spaced checkbox-group-inline">
              <label class="input-label">
                <i class="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  v-model="seller.isActive"
                  class="checkbox-input"
                />
                <span class="checkbox-text">
                  Vendedor Ativo
                </span>
              </label>
            </div>
          </div>

          <!-- Detalhes da Modalidade -->
          <div v-if="seller.modality" class="modality-details">
            <h4 class="details-title">
              <i class="fa-solid fa-info-circle"></i>
              Detalhes da Modalidade
            </h4>
            <div class="details-table-container">
              <table class="details-table">
                <thead>
                  <tr>
                    <th>Modalidade</th>
                    <th>Cotação</th>
                    <th>Prêmio Máx.</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in seller.modality.regionModalityDetail" :key="m.modalityId">
                    <td>{{ m.modalityName }}</td>
                    <td>R$ {{ m.price }}</td>
                    <td>R$ {{ m.maxPrize }}</td>
                    <td>
                      <span :class="['status-badge', m.isActive ? 'badge-success' : 'badge-danger']">
                        {{ m.isActive ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="seller.id ? updateSeller() : insertSeller()"
            :disabled="saving"
          >
            <i :class="saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-save'"></i>
            {{ saving ? 'Salvando...' : (seller.id ? 'Atualizar' : 'Salvar') }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="reset()"
            :disabled="saving"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { vMaska } from "maska/vue";
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";
import jsFunctions from "@/services/jsFunctions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const TABLE_ZOOM_STORAGE_KEY = "ab.resultsTableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

export default {
  name: "ViewSellers",
  directives: {
    maska: vMaska
  },
  data: () => ({
    quickFilter: "",
    sellers: [],
    modalities: [],
    regions: [],
    collectors: [],
    loading: true,
    saving: false,
    seller: {},
    loadingCep: false,
    cepError: "",
    selectedTab: 1,
    pageIndex: 1,
    pageSize: 10,
    currentRegion: null,
    currentUser: null,
    userResponsibleRegions: [], // Array das regiões pelas quais o usuário é responsável
    userBancaId: null, // BancaId do usuário BANCA
    
    // Regiões de descarga disponíveis
    descargaRegions: [],
    
    // Timer para atualização automática de saldos
    balanceUpdateTimer: null,
    isUpdatingBalances: false, // Manter para controle interno de requisições duplicadas
    
    // Referência para o listener de visibilidade
    visibilityChangeHandler: null,
    selectedRegionSnapshot: null,
    selectedBancaSnapshot: null,
    tableZoom: 100,
  }),
  computed: {
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },

    currentSelectedRegion() {
      return this.selectedRegionSnapshot || this.readSelectedRegionFromStorage();
    },

    currentSelectedBanca() {
      return this.selectedBancaSnapshot || this.readSelectedBancaFromStorage();
    },

    filteredSellers() {
      let sellers = this.sellers;

      if (this.isCobrador) {
        // COBRADOR: só vendedores vinculados a ele (lista preferencialmente via GetSellersByCollector)
        const collectorId = String(this.currentUser?.id || '');
        sellers = sellers.filter(seller => {
          if (!seller || seller.id == null) return false;
          if (seller.collectorId != null || seller.CollectorId != null) {
            return String(seller.collectorId || seller.CollectorId) === collectorId;
          }
          // Se veio da API de vínculo sem collectorId, mantém (já é lista do cobrador)
          return true;
        });

        if (this.currentSelectedRegion) {
          sellers = sellers.filter(seller =>
            String(seller.regionId) === String(this.currentSelectedRegion.id)
          );
        }
      } else if (this.isGerente) {
        sellers = sellers.filter(seller =>
          this.userResponsibleRegions.some(id => String(id) === String(seller.regionId))
        );

        if (this.currentSelectedRegion) {
          sellers = sellers.filter(seller =>
            String(seller.regionId) === String(this.currentSelectedRegion.id)
          );
        }
      } else if (this.isBanca) {
        const allowedRegionIds = this.regions
          .filter(region => String(region.bancaId) === String(this.userBancaId))
          .map(region => String(region.id));

        sellers = sellers.filter(seller =>
          allowedRegionIds.includes(String(seller.regionId))
        );

        if (this.currentSelectedRegion) {
          sellers = sellers.filter(seller =>
            String(seller.regionId) === String(this.currentSelectedRegion.id)
          );
        }
      } else if (this.isAdmin) {
        if (this.currentSelectedBanca) {
          sellers = sellers.filter(seller =>
            String(seller.bancaId) === String(this.currentSelectedBanca.id)
          );
        }

        if (this.currentSelectedRegion) {
          sellers = sellers.filter(seller =>
            String(seller.regionId) === String(this.currentSelectedRegion.id)
          );
        }
      }

      if (this.quickFilter) {
        const filter = this.quickFilter.toUpperCase();
        sellers = sellers.filter(seller =>
          (seller.name && seller.name.toUpperCase().includes(filter)) ||
          (seller.login && seller.login.toUpperCase().includes(filter)) ||
          (filter === "ATIVO" && seller.isActive) ||
          (filter === "INATIVO" && !seller.isActive) ||
          (filter === "NÃO" && !seller.payCollectorCommission)
        );
      }

      return [...sellers].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
      );
    },

    totalRows() {
      return this.filteredSellers.length;
    },

    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize) || 0;
    },

    startItem() {
      return this.totalRows === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
    },

    endItem() {
      if (this.totalRows === 0) return 0;
      return Math.min(this.pageIndex * this.pageSize, this.totalRows);
    },

    paginatedSellers() {
      const start = (this.pageIndex - 1) * this.pageSize;
      return this.filteredSellers.slice(start, start + this.pageSize);
    },
    
    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    isModalityMissingForNewSeller() {
      if (this.seller.id) return false;
      const id = this.seller.regionModalityParameterId;
      return id == null || id === '' || id === 'null';
    },

    /** Modalidades da região com jogo ativo; se nenhuma, usa a lista completa. */
    activeModalities() {
      const all = this.modalities || [];
      const withActive = all.filter((m) => {
        const details = m.regionModalityDetail || m.RegionModalityDetail || [];
        if (!details.length) return true;
        return details.some((d) => d.isActive === true || d.IsActive === true);
      });
      return withActive.length > 0 ? withActive : all;
    },
    canCreateEditSellers() {
      // ADMIN: acesso total
      // BANCA e GERENTE: podem criar/editar/excluir
      // COBRADOR: pode criar/editar
      return this.isAdmin || this.isBanca || this.isGerente || this.isCobrador;
    },

    canDeleteSellers() {
      // ADMIN: pode excluir
      // BANCA e GERENTE: podem excluir
      // COBRADOR: não pode excluir
      return this.isAdmin || this.isBanca || this.isGerente;
    },

    availableRegionsForUser() {
      if (this.isAdmin) {
        return this.regions;
      } else if (this.isBanca) {
        // Para BANCA: filtrar regiões onde BancaId corresponde ao userBancaId
        return this.regions.filter(region => 
          region.bancaId === this.userBancaId
        );
      } else {
        // Para GERENTE/COBRADOR, filtrar apenas as regiões do usuário
        return this.regions.filter(region => 
          this.userResponsibleRegions.includes(region.id)
        );
      }
    },

    filteredCollectors() {
      // Verificar se a região está definida
      if (!this.seller.regionId || this.seller.regionId === 'null' || this.seller.regionId === null) {
        return [];
      }
      
      // Comparar como strings (regionId são UUIDs)
      const regionId = String(this.seller.regionId);
      
      // Filtrar cobradores pela região selecionada
      return this.collectors.filter(collector => {
        return String(collector.regionId) === regionId;
      });
    },

    availableDescargaRegions() {
      // Filtrar regiões excluindo a região atual do vendedor
      return this.descargaRegions.filter(region => 
        !this.seller.regionId || region.id !== this.seller.regionId
      );
    }
  },
  mounted() {
    this.loading = true;
    this.loadTableZoom();
    this.syncSelectedRegionSnapshot();
    this.syncSelectedBancaSnapshot();
    this.currentUser = JSON.parse(localStorage.getItem("user"));
    
    if (this.currentUser) {
      this.loadUserResponsibleRegions();
    } else {
      this.loading = false;
      toast.error("Usuário não logado!");
    }
    
    // Listener para mudanças na região/banca selecionada
    window.addEventListener('storage', (e) => {
      if (e.key === 'currentRegion') {
        this.syncSelectedRegionSnapshot();
        this.pageIndex = 1;
        this.searchModalities();
        this.searchCollectors();
        this.stopBalanceUpdates();
        if (this.selectedTab === 1 && this.sellers.length > 0) {
          this.startBalanceUpdates();
        }
      }
      if (e.key === 'selectedBanca') {
        this.syncSelectedBancaSnapshot();
        this.pageIndex = 1;
      }
    });

    window.addEventListener('regionChanged', this.onRegionOrBancaChanged);
    window.addEventListener('regionAutoSelected', this.onRegionOrBancaChanged);
    window.addEventListener('bancaChanged', this.onRegionOrBancaChanged);
    
    // Listener para visibilidade da página (otimização de performance)
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // Página ficou oculta, pausar atualizações
        this.stopBalanceUpdates();
      } else {
        // Página ficou visível, retomar atualizações se estiver na aba de listagem
        if (this.selectedTab === 1 && this.sellers.length > 0) {
          this.startBalanceUpdates();
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  },
  
  watch: {
    quickFilter() {
      this.pageIndex = 1;
    },
    totalPages(newTotal) {
      if (this.pageIndex > newTotal && newTotal > 0) {
        this.pageIndex = newTotal;
      } else if (newTotal === 0) {
        this.pageIndex = 1;
      }
    },
    currentSelectedRegion(newRegion, oldRegion) {
      this.pageIndex = 1;
      if (String(newRegion?.id || '') !== String(oldRegion?.id || '')) {
        this.searchModalities();
      }
    },
    currentSelectedBanca() {
      this.pageIndex = 1;
    },
    // Pausar/retomar atualizações baseado na aba selecionada
    selectedTab(newTab, oldTab) {
      if (newTab === 1) {
        // Voltou para a aba de listagem, retomar atualizações se houver vendedores
        if (this.sellers.length > 0) {
          this.startBalanceUpdates();
        }
      } else {
        // Saiu da aba de listagem, pausar atualizações
        this.stopBalanceUpdates();
      }
    },
    
    // Watch para detectar mudanças na regionId do seller e atualizar cobradores
    'seller.regionId': {
      handler(newRegionId) {
        // Quando a região mudar, limpar o cobrador selecionado se ele não pertencer à nova região
        if (this.seller.collectorId && !this.filteredCollectors.some(c => String(c.id) === String(this.seller.collectorId))) {
          this.seller.collectorId = null;
        }
      },
      immediate: false
    }
  },
  beforeUnmount() {
    // Remover listener ao destruir o componente
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('regionChanged', this.onRegionOrBancaChanged);
    window.removeEventListener('regionAutoSelected', this.onRegionOrBancaChanged);
    window.removeEventListener('bancaChanged', this.onRegionOrBancaChanged);
    
    // Remover listener de visibilidade da página
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    
    // Limpar timer de atualização de saldos
    this.stopBalanceUpdates();
  },
  methods: {
    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (!Number.isNaN(saved) && saved >= TABLE_ZOOM_MIN && saved <= TABLE_ZOOM_MAX) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },

    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },

    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },

    readSelectedRegionFromStorage() {
      try {
        const savedRegion = localStorage.getItem('currentRegion');
        return savedRegion ? JSON.parse(savedRegion) : null;
      } catch {
        return null;
      }
    },

    readSelectedBancaFromStorage() {
      try {
        const savedBanca = localStorage.getItem('selectedBanca');
        return savedBanca ? JSON.parse(savedBanca) : null;
      } catch {
        return null;
      }
    },

    syncSelectedRegionSnapshot() {
      this.selectedRegionSnapshot = this.readSelectedRegionFromStorage();
    },

    syncSelectedBancaSnapshot() {
      this.selectedBancaSnapshot = this.readSelectedBancaFromStorage();
    },

    onRegionOrBancaChanged() {
      this.syncSelectedRegionSnapshot();
      this.syncSelectedBancaSnapshot();
      this.pageIndex = 1;
      this.stopBalanceUpdates();
      // Recarregar modalidades/cobradores da região ativa (cobrador troca de região com frequência)
      this.searchModalities();
      this.searchCollectors();
      if (this.selectedTab === 1 && this.sellers.length > 0) {
        this.startBalanceUpdates();
      }
    },

    onRegionChanged() {
      this.onRegionOrBancaChanged();
    },

    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.pageIndex = newPage;
      }
    },

    createSeller() {
      if (!this.canCreateEditSellers) {
        toast.error("Você não tem permissão para criar vendedores!");
        return;
      }

      this.syncSelectedRegionSnapshot();

      // Verificar se há região ativa selecionada
      if (!this.currentSelectedRegion) {
        toast.error("É necessário selecionar uma região ativa para criar um vendedor!");
        this.showRegionSelectionMessage();
        return;
      }

      // Pausar atualizações automáticas durante edição
      this.stopBalanceUpdates();

      // Usar Vue.set ou atribuição reativa para garantir que o Vue detecte as mudanças
      this.seller = {
        isActive: false, // Padrão como inativo (desmarcado)
        comission: 0, // Valor numérico sem formatação
        collectorCommission: 0, // Valor numérico sem formatação
        collectorCommissionType: true, // Default to Bruto
        // Cobrador sempre vincula a si mesmo; demais perfis escolhem no select
        collectorId: this.isCobrador ? this.currentUser.id : null,
        walletLimit: jsFunctions.setCurrencyString(0).replace('R$ ', ''), // Valor formatado usando função padrão
        walletBalance: 0, // Default wallet balance
        regionId: this.currentSelectedRegion.id, // Usar diretamente a região ativa
        descargaReturnPercent: 0, // Valor numérico sem formatação
        idRegionDescarga: null, // Região de descarga (opcional)
        cep: "",
        street: "",
        addressNumber: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
      };
      this.cepError = "";
      this.loadingCep = false;

      // Garantir modalidades da região ativa (cobrador/gerente dependem disso no select)
      this.searchModalities().then(() => this.autoSelectModalityIfSingle());
      
      // Forçar atualização do Vue para garantir que filteredCollectors seja recalculado
      this.$nextTick(() => {
        this.selectedTab = 2;
      });
    },
    
    handleCadastroTabClick() {
      // Se já está editando um vendedor, apenas mudar a tab
      if (this.seller.id) {
        this.selectedTab = 2;
      } else {
        // Se não está editando, chamar createSeller para inicializar corretamente
        this.createSeller();
      }
    },
    
    editSeller(seller) {
      if (!this.canCreateEditSellers) {
        toast.error("Você não tem permissão para editar vendedores!");
        return;
      }

      // Pausar atualizações automáticas durante edição
      this.stopBalanceUpdates();

      this.seller = { ...seller };
      
      // Não carregar a senha para edição - ela deve ser deixada em branco
      this.seller.password = "";
      this.cepError = "";
      this.loadingCep = false;

      // Endereço — garantir strings vazias se vier null
      this.seller.cep = seller.cep || "";
      this.seller.street = seller.street || "";
      this.seller.addressNumber = seller.addressNumber || "";
      this.seller.complement = seller.complement || "";
      this.seller.neighborhood = seller.neighborhood || "";
      this.seller.city = seller.city || "";
      this.seller.state = seller.state || "";
      
      // Comissão - manter valor numérico sem formatação
      if (!this.seller.comission || this.seller.comission === null) {
        this.seller.comission = 0;
      }
      
      // Comissão do cobrador - manter valor numérico sem formatação
      if (!this.seller.collectorCommission || this.seller.collectorCommission === null) {
        this.seller.collectorCommission = 0;
      }
      
      // Limite da carteira - formatar usando a função padrão do sistema
      this.seller.walletLimit = jsFunctions.setCurrencyString(this.seller.walletLimit || 0).replace('R$ ', '');
      
      // Formatar saldo da carteira (deixar como está para exibição)
      if (typeof this.seller.walletBalance === 'undefined' || this.seller.walletBalance === null) {
        this.seller.walletBalance = 0;
      }
      
      // % de retorno da descarga - manter valor numérico sem formatação
      if (!this.seller.descargaReturnPercent || this.seller.descargaReturnPercent === null) {
        this.seller.descargaReturnPercent = 0;
      }
      
      this.selectedTab = 2;
      // Garante modalidades da região ativa antes de resolver o select
      this.searchModalities().then(() => this.selectModality());
    },
    
    reset() {
      this.seller = {};
      this.cepError = "";
      this.loadingCep = false;
      this.selectedTab = 1;
      
      // Retomar atualizações automáticas quando voltar para listagem
      if (this.sellers.length > 0) {
        this.startBalanceUpdates();
      }
    },

    onCepInput() {
      this.cepError = "";
      const digits = String(this.seller.cep || "").replace(/\D/g, "");
      if (digits.length === 8) {
        this.lookupCep();
      }
    },

    async lookupCep() {
      const digits = String(this.seller.cep || "").replace(/\D/g, "");
      this.cepError = "";

      if (digits.length !== 8) {
        return;
      }

      // Evita buscas repetidas do mesmo CEP
      if (this._lastCepLookup === digits && this.loadingCep) {
        return;
      }
      this._lastCepLookup = digits;

      this.loadingCep = true;
      try {
        const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
        if (!response.ok) {
          throw new Error("Falha ao consultar CEP");
        }
        const data = await response.json();
        if (data.erro) {
          this.cepError = "CEP não encontrado.";
          return;
        }

        // Se o usuário já mudou o CEP enquanto buscava, ignora o resultado
        const currentDigits = String(this.seller.cep || "").replace(/\D/g, "");
        if (currentDigits !== digits) {
          return;
        }

        this.seller.cep = digits.replace(/(\d{5})(\d{3})/, "$1-$2");
        this.seller.street = data.logradouro || "";
        this.seller.neighborhood = data.bairro || "";
        this.seller.city = data.localidade || "";
        this.seller.state = data.uf || "";
        // Número e complemento continuam manuais
      } catch {
        this.cepError = "Não foi possível buscar o CEP. Preencha manualmente.";
      } finally {
        this.loadingCep = false;
      }
    },
    
    selectModality() {
      if (this.seller.regionModalityParameterId == "null") {
        this.seller.regionModalityParameterId = null;
        this.seller.modality = null;
      } else {
        this.seller.modality = this.modalities.filter((c) => c.id == this.seller.regionModalityParameterId)[0];
      }
    },

    /** Na criação, se a região tiver só 1 modalidade ativa, já preenche o select. */
    autoSelectModalityIfSingle() {
      if (this.seller?.id) return;
      const list = this.activeModalities;
      if (list.length !== 1) return;
      this.seller.regionModalityParameterId = list[0].id;
      this.selectModality();
    },
    
    async validateSeller() {
      if (this.seller.name == null || this.seller.name == "" || 
          this.seller.regionId == null || this.seller.regionId == "null" ||
          this.seller.login == null || this.seller.login == "" ||
          this.seller.walletLimit == null || this.seller.walletLimit == "") {
        toast.error("Por favor, preencha todos os campos obrigatórios!");
        return false;
      }

      // Modalidade obrigatória ao criar novo vendedor
      if (!this.seller.id && this.isModalityMissingForNewSeller) {
        toast.error("Selecione uma modalidade para cadastrar o vendedor.");
        return false;
      }
      
      // Validar se a região é OnlyDescarga e se o campo região de descarga é obrigatório
      const currentRegion = this.currentSelectedRegion;
      if (currentRegion && currentRegion.onlyDescarga && (!this.seller.idRegionDescarga || this.seller.idRegionDescarga === null)) {
        toast.error("Para Regiões de Descarga é obrigatório preencher o campo de Região de Descarga do Vendedor.");
        return false;
      }
      
      // Garantir que isActive tenha um valor padrão se não estiver definido
      if (this.seller.isActive == null || this.seller.isActive == undefined) {
        this.seller.isActive = false; // Padrão como inativo
      }
      
      // Validar senha apenas para novos vendedores (sem ID)
      if (!this.seller.id && (this.seller.password == null || this.seller.password == "")) {
        toast.error("Por favor, preencha todos os campos obrigatórios!");
        return false;
      }

      // Validar tamanho mínimo da senha (6 caracteres)
      if (this.seller.password && this.seller.password.length < 6) {
        toast.error("A senha deve ter pelo menos 6 caracteres!");
        return false;
      }

      // Verificar se o login já existe
      const loginExists = await this.checkLoginExists(this.seller.login);
      if (loginExists) {
        toast.error(`O login "${this.seller.login}" já está sendo usado por outro vendedor!`);
        return false;
      }
      
      return true;
    },
    
    async loadUserResponsibleRegions() {
      try {
        if (this.isAdmin) {
          // ADMIN pode ver todas as regiões
          await this.searchRegions();
          this.searchModalities();
          this.searchCollectors();
          this.searchSellers();
          this.loadDescargaRegions();
        } else if (this.isBanca) {
          // Para BANCA, buscar o BancaId do usuário
          const response = await mainService.getUserBancaId(this.currentUser.id);
          this.userBancaId = response.data;

          if (this.userBancaId) {
            await this.searchRegions();
            this.searchModalities();
            this.searchCollectors();
            this.searchSellers();
            this.loadDescargaRegions();
          } else {
            this.loading = false;
            toast.error("Usuário BANCA não possui BancaId definido!");
          }
        } else {
          // Para GERENTE/COBRADOR, buscar todas as regiões do usuário
          const response = await mainService.getUserRegions(this.currentUser.id);
          const userRegions = response.data || [];
          
          // Incluir também a região principal se existir
          this.userResponsibleRegions = userRegions.map(r => r.id);
          if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
            this.userResponsibleRegions.push(this.currentUser.regionId);
          }
          
          if (this.userResponsibleRegions.length > 0) {
            await this.searchRegions();
            this.searchModalities();
            this.searchCollectors();
            this.searchSellers();
            this.loadDescargaRegions();
          } else {
            this.loading = false;
            toast.error("Usuário não possui regiões definidas!");
          }
        }
      } catch (error) {
        this.loading = false;
        toast.error("Erro ao carregar regiões do usuário!");
      }
    },

    searchRegions() {
      return mainService
        .searchRegions()
        .then((response) => {
          this.regions = response.data || [];
        })
        .catch((error) => {
          this.regions = [];
        });
    },

    searchModalities() {
      this.syncSelectedRegionSnapshot();
      const region = this.currentSelectedRegion;
      if (!region?.id) {
        this.modalities = [];
        return Promise.resolve();
      }

      return mainService
        .searchRegionModalities(region.id)
        .then((response) => {
          const data = response?.data;
          this.modalities = Array.isArray(data)
            ? data
            : Array.isArray(data?.result)
              ? data.result
              : [];
          // Se estiver criando vendedor e só houver 1 modalidade ativa, auto-preenche
          this.autoSelectModalityIfSingle();
        })
        .catch((error) => {
          this.modalities = [];
          toast.error(handleErrorMessage(error));
        });
    },

    searchCollectors() {
      mainService
        .searchCollectors()
        .then((response) => {
          this.collectors = response.data;
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        });
    },
    
    searchSellers() {
      this.loading = true;

      const mapSeller = (seller) => {
        const sellerRegion = this.regions.find(r => String(r.id) === String(seller.regionId));
        return {
          ...seller,
          collectorId: seller.collectorId || seller.CollectorId || null,
          bancaId: sellerRegion?.bancaId || null,
        };
      };

      // COBRADOR: carrega apenas vendedores vinculados
      const request = this.isCobrador && this.currentUser?.id
        ? mainService.getSellersByCollector(this.currentUser.id)
        : mainService.searchAllSellers();

      request
        .then((response) => {
          const list = Array.isArray(response.data) ? response.data : [];
          this.sellers = list.map(seller => {
            const mapped = mapSeller(seller);
            if (this.isCobrador && this.currentUser?.id) {
              mapped.collectorId =
                mapped.collectorId || this.currentUser.id;
            }
            return mapped;
          });
          this.pageIndex = 1;
          
          // Iniciar atualização automática de saldos após carregamento inicial
          this.startBalanceUpdates();
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
          this.sellers = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Iniciar atualização automática de saldos
    startBalanceUpdates() {
      // Parar qualquer timer existente primeiro
      this.stopBalanceUpdates();
      
      // Criar novo timer para atualizar a cada 5 segundos
      this.balanceUpdateTimer = setInterval(() => {
        this.updateSellerBalances();
      }, 5000); // 5000ms = 5 segundos
    },

    // Parar atualização automática de saldos
    stopBalanceUpdates() {
      if (this.balanceUpdateTimer) {
        clearInterval(this.balanceUpdateTimer);
        this.balanceUpdateTimer = null;
      }
    },

    // Atualizar apenas os saldos dos vendedores (sem recarregar toda a lista)
    async updateSellerBalances() {
      // Não atualizar se já estiver carregando ou se não houver vendedores
      if (this.isUpdatingBalances || this.loading || this.sellers.length === 0) {
        return;
      }
      
      this.isUpdatingBalances = true;
      
      try {
        // Buscar saldos atualizados conforme o filtro atual
        let response;
        if (this.isCobrador && this.currentUser?.id) {
          response = await mainService.getSellersByCollector(this.currentUser.id);
        } else if (this.currentSelectedRegion) {
          response = await mainService.searchSellersByRegion(this.currentSelectedRegion.id);
        } else {
          response = await mainService.searchAllSellers();
        }
        const updatedSellers = response.data || [];
        
        // Atualizar apenas os saldos dos vendedores existentes
        this.sellers = this.sellers.map(seller => {
          const updatedSeller = updatedSellers.find(s => s.id === seller.id);
          if (updatedSeller) {
            // Manter todos os dados existentes, atualizar apenas o saldo
            return {
              ...seller,
              walletBalance: updatedSeller.walletBalance
            };
          }
          return seller;
        });
        
        // Adicionar novos vendedores se houver (quando busca completa / cobrador)
        if (!this.currentSelectedRegion || this.isCobrador) {
          const existingIds = this.sellers.map(s => s.id);
          const newSellers = updatedSellers
            .filter(s => !existingIds.includes(s.id))
            .map(seller => {
              const sellerRegion = this.regions.find(r => String(r.id) === String(seller.regionId));
              return {
                ...seller,
                collectorId:
                  seller.collectorId ||
                  seller.CollectorId ||
                  (this.isCobrador ? this.currentUser.id : null),
                bancaId: sellerRegion?.bancaId || null
              };
            });
          if (newSellers.length > 0) {
            this.sellers = [...this.sellers, ...newSellers];
          }
          
          // Remover vendedores que não existem mais
          const updatedIds = updatedSellers.map(s => s.id);
          this.sellers = this.sellers.filter(s => updatedIds.includes(s.id));
        }
        
      } catch (error) {
        // Em caso de erro, não mostrar toast para não incomodar o usuário
        // mas parar as atualizações se for erro crítico
        if (error.response && error.response.status >= 500) {
          this.stopBalanceUpdates();
        }
      } finally {
        this.isUpdatingBalances = false;
      }
    },
    
    async insertSeller() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditSellers) {
          toast.error("Você não tem permissão para criar vendedores!");
          return;
        }

        // Verificar se há região ativa selecionada
        this.syncSelectedRegionSnapshot();
        if (!this.currentSelectedRegion) {
          toast.error("É necessário selecionar uma região ativa para criar um vendedor!");
          this.showRegionSelectionMessage();
          return;
        }

        // Garantir que o vendedor está associado à região ativa
        this.seller.regionId = this.currentSelectedRegion.id;

        // Cobrador só pode criar vendedores vinculados a si mesmo
        if (this.isCobrador) {
          this.seller.collectorId = this.currentUser.id;
        }

        if (!(await this.validateSeller())) {
          return;
        }

        // Garantir que comission seja número
        this.seller.comission = parseFloat(this.seller.comission) || 0;
        
        // Garantir que collectorCommission seja número  
        this.seller.collectorCommission = parseFloat(this.seller.collectorCommission) || 0;
        
        // Converter walletLimit de string formatada para número
        const walletLimitStr = typeof this.seller.walletLimit === 'string' 
          ? this.seller.walletLimit 
          : String(this.seller.walletLimit || 0);
        this.seller.walletLimit = parseFloat(walletLimitStr.replace(/[^\d,]/g, '').replace(",", "."));
        
        // Garantir que descargaReturnPercent seja número
        this.seller.descargaReturnPercent = parseFloat(this.seller.descargaReturnPercent) || 0;
        
        // Para novo vendedor, o saldo inicial da carteira é igual ao limite
        this.seller.walletBalance = this.seller.walletLimit;
        
        // Garantir que o walletLimit seja um número válido
        if (isNaN(parseFloat(this.seller.walletLimit))) {
          toast.error("Valor do limite da carteira é inválido!");
          return;
        }
        
        // Definir payCollectorCommission baseado no valor da collectorCommission
        this.seller.payCollectorCommission = this.seller.collectorCommission > 0;

        try {
          await mainService.insertSeller(this.seller);
          toast.success("Vendedor cadastrado com sucesso!");
          this.seller = {};
          this.searchSellers(); // Isso já vai iniciar as atualizações automáticas
          this.selectedTab = 1;
        } catch (error) {
          // Verificar se é erro de validação de senha
          if (error.response && error.response.status === 400) {
            const errorMessage = handleErrorMessage(error);
            if (errorMessage.includes('senha') || errorMessage.includes('password') || 
                (this.seller.password && this.seller.password.length < 6)) {
              toast.error("A senha deve ter pelo menos 6 caracteres!");
            } else {
              toast.error(errorMessage);
            }
          } else {
            toast.error(handleErrorMessage(error));
          }
          // Em caso de erro, retomar atualizações para não deixar a tela "parada"
          this.startBalanceUpdates();
        }
      } finally {
        this.saving = false;
      }
    },
    
    async updateSeller() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditSellers) {
          toast.error("Você não tem permissão para editar vendedores!");
          return;
        }

        // Cobrador não pode transferir o vendedor para outro cobrador
        if (this.isCobrador) {
          this.seller.collectorId = this.currentUser.id;
        }

        if (!(await this.validateSeller())) {
          return;
        }

        // Garantir que comission seja número
        this.seller.comission = parseFloat(this.seller.comission) || 0;
        
        // Garantir que collectorCommission seja número
        this.seller.collectorCommission = parseFloat(this.seller.collectorCommission) || 0;
        
        // Converter walletLimit de string formatada para número
        const walletLimitStr = typeof this.seller.walletLimit === 'string' 
          ? this.seller.walletLimit 
          : String(this.seller.walletLimit || 0);
        this.seller.walletLimit = parseFloat(walletLimitStr.replace(/[^\d,]/g, '').replace(",", "."));
        
        // Garantir que descargaReturnPercent seja número
        this.seller.descargaReturnPercent = parseFloat(this.seller.descargaReturnPercent) || 0;
        
        // Garantir que o walletLimit seja um número válido
        if (isNaN(this.seller.walletLimit)) {
          toast.error("Valor do limite da carteira é inválido!");
          return;
        }
        
        // O walletBalance não deve ser alterado na edição (mantém o valor atual)
        // this.seller.walletBalance permanece como está
        
        // Definir payCollectorCommission baseado no valor da collectorCommission
        this.seller.payCollectorCommission = this.seller.collectorCommission > 0;
        
        // Se a senha estiver vazia, enviar null para manter a senha existente
        if (!this.seller.password || this.seller.password.trim() === "") {
          this.seller.password = null;
        }

        try {
          await mainService.updateSeller(this.seller);
          toast.success("Vendedor atualizado com sucesso!");
          this.seller = {};
          this.searchSellers(); // Isso já vai iniciar as atualizações automáticas
          this.selectedTab = 1;
        } catch (error) {
          // Verificar se é erro de validação de senha
          if (error.response && error.response.status === 400) {
            const errorMessage = handleErrorMessage(error);
            if (errorMessage.includes('senha') || errorMessage.includes('password') || 
                (this.seller.password && this.seller.password.length < 6)) {
              toast.error("A senha deve ter pelo menos 6 caracteres!");
            } else {
              toast.error(errorMessage);
            }
          } else {
            toast.error(handleErrorMessage(error));
          }
          // Em caso de erro, retomar atualizações para não deixar a tela "parada"
          this.startBalanceUpdates();
        }
      } finally {
        this.saving = false;
      }
    },
    
    deleteSeller(idSeller) {
      if (!this.canDeleteSellers) {
        toast.error("Você não tem permissão para excluir vendedores!");
        return;
      }

      this.$confirm({
        message: "Confirma a exclusão?",
        button: { no: "Não", yes: "Sim" },
        callback: (confirm) => {
          if (confirm) {
            mainService
              .deleteSeller(idSeller)
              .then(() => {
                toast.success("Vendedor excluído com sucesso!");
                this.searchSellers();
                this.selectedTab = 1;
              })
              .catch((error) => {
                toast.error(handleErrorMessage(error));
              })
              .finally(() => {});
          }
        },
      });
    },

    getRegionName(regionId) {
      // Buscar nome da região na lista de regiões carregadas
      const region = this.regions.find(r => r.id === regionId);
      return region ? region.name : 'N/A';
    },

    getCollectorName(collectorId) {
      const collector = this.collectors.find(c => c.id === collectorId);
      return collector ? collector.name : 'Não definido';
    },

    formatCommission(commission) {
      if (commission === null || commission === undefined) {
        return '0.00';
      }
      
      // Retorna valor numérico com ponto decimal como no banco
      return parseFloat(commission).toFixed(2);
    },

    formatCurrency(value) {
      if (value === null || value === undefined) {
        return '0,00';
      }
      
      // Se já é uma string formatada (com vírgula), retorna como está
      if (typeof value === 'string' && value.includes(',')) {
        return value;
      }
      
      // Se é um número, formata com duas casas decimais
      return parseFloat(value).toFixed(2).replace('.', ',');
    },

    async generateLogin() {
      try {
        // Gerar código automaticamente
        const newLogin = await this.generateUniqueLogin();
        this.seller.login = newLogin;
        toast.success(`Código ${newLogin} gerado com sucesso!`);
      } catch (error) {
        toast.error("Erro ao gerar código: " + error.message);
      }
    },

    async generateUniqueLogin() {
      // Começar com 4 dígitos e ir aumentando se necessário
      let digits = 4;
      let maxAttempts = 1000; // Limite de tentativas para evitar loop infinito
      let attempts = 0;

      while (attempts < maxAttempts) {
        attempts++;
        
        // Gerar número aleatório com a quantidade de dígitos especificada
        const min = Math.pow(10, digits - 1);
        const max = Math.pow(10, digits) - 1;
        const randomCode = Math.floor(Math.random() * (max - min + 1)) + min;
        const loginCode = randomCode.toString();

        // Verificar se o código já existe
        const exists = await this.checkLoginExists(loginCode);
        
        if (!exists) {
          return loginCode;
        }

        // Se tentou muitas vezes com o mesmo número de dígitos, aumentar
        if (attempts > 100 && digits < 8) {
          digits++;
          attempts = 0; // Reset tentativas para novo número de dígitos
        }
      }

      throw new Error("Não foi possível gerar um código único após várias tentativas");
    },

    async checkLoginExists(login) {
      try {
        // Usar a API específica para verificar se o login já existe
        const excludeId = this.seller.id || null; // Excluir o próprio vendedor em caso de edição
        const response = await mainService.checkLoginExists(login, excludeId);
        
        return response.data === true;
      } catch (error) {
        // Em caso de erro, assumir que não existe para não bloquear a geração
        return false;
      }
    },

    showPermissionError() {
      toast.error("Você não tem permissão para criar/editar vendedores!");
    },

    showRegionSelectionMessage() {
      if (this.isAdmin) {
        toast.info("Para criar um vendedor, selecione uma região no filtro localizado no topo da página.");
      } else {
        toast.info("Entre em contato com o administrador para configurar sua região ativa.");
      }
    },

    // Carregar regiões de descarga disponíveis
    async loadDescargaRegions() {
      try {
        const response = await mainService.searchRegions();
        this.descargaRegions = response.data || [];
      } catch (error) {
        this.descargaRegions = [];
      }
    },

    formatWalletLimitInput(event) {
      // Permitir apenas números, vírgula e ponto
      let value = event.target.value.replace(/[^\d,]/g, '');
      
      // Garantir apenas uma vírgula
      const parts = value.split(',');
      if (parts.length > 2) {
        value = parts[0] + ',' + parts.slice(1).join('');
      }
      
      // Limitar casas decimais a 2
      if (parts[1] && parts[1].length > 2) {
        value = parts[0] + ',' + parts[1].substring(0, 2);
      }
      
      this.seller.walletLimit = value;
    },

    formatWalletLimitBlur() {
      // Formatar corretamente ao sair do campo usando a função padrão do sistema
      if (this.seller.walletLimit) {
        const numericValue = parseFloat(this.seller.walletLimit.replace(',', '.')) || 0;
        this.seller.walletLimit = jsFunctions.setCurrencyString(numericValue).replace('R$ ', '');
      } else {
        this.seller.walletLimit = "0,00";
      }
    },

    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },

    toggleSellerStatus(sellerId, newStatus) {
      if (!this.canCreateEditSellers) {
        toast.error("Você não tem permissão para alterar o status de vendedores!");
        return;
      }

      const action = newStatus ? 'ativar' : 'desativar';
      const seller = this.sellers.find(s => s.id === sellerId);
      const sellerName = seller ? seller.name : 'este vendedor';

      this.$confirm({
        message: `Confirma ${action} o vendedor "${sellerName}"?`,
        button: { no: "Não", yes: "Sim" },
        callback: (confirm) => {
          if (confirm) {
            mainService
              .toggleSellerStatus(sellerId, newStatus)
              .then(() => {
                toast.success(`Vendedor ${newStatus ? 'ativado' : 'desativado'} com sucesso!`);
                this.searchSellers();
              })
              .catch((error) => {
                toast.error(handleErrorMessage(error));
              });
          }
        },
      });
    },

    getExportColumns() {
      return [
        { key: "name", label: "Nome" },
        { key: "login", label: "Login" },
        { key: "region", label: "Região" },
        { key: "phone", label: "Telefone" },
        { key: "point", label: "Ponto" },
        { key: "comission", label: "Comissão" },
        { key: "collectorCommission", label: "Comissão Cobrador" },
        { key: "collectorCommissionType", label: "Tipo Comissão" },
        { key: "walletLimit", label: "Limite Carteira" },
        { key: "walletBalance", label: "Saldo Carteira" },
        { key: "collector", label: "Cobrador" },
        { key: "modality", label: "Modalidade" },
        { key: "terminal", label: "Terminal" },
        { key: "status", label: "Status" },
      ];
    },

    buildExportRow(seller) {
      return [
        seller.name || "-",
        seller.login || "-",
        this.getRegionName(seller.regionId),
        seller.phone || "-",
        seller.point || "-",
        `${this.formatCommission(seller.comission)}%`,
        seller.payCollectorCommission === true ? `${this.formatCommission(seller.collectorCommission)}%` : "NÃO",
        seller.collectorCommissionType ? "Bruto" : "Líquido",
        `R$ ${this.formatCurrency(seller.walletLimit)}`,
        `R$ ${this.formatCurrency(seller.walletBalance)}`,
        this.getCollectorName(seller.collectorId),
        seller.modality?.name || "-",
        seller.terminal?.key || "-",
        seller.isActive ? "Ativo" : "Inativo",
      ];
    },

    printReport() {
      const sellers = this.filteredSellers;
      if (!sellers || sellers.length === 0) {
        toast.warning("Não há dados para imprimir");
        return;
      }

      const printWindow = window.open("", "_blank");
      const printContent = this.generatePrintHTML();

      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    generatePrintHTML() {
      const sellers = this.filteredSellers;
      const columns = this.getExportColumns();

      let tableRows = "";
      sellers.forEach(seller => {
        const cells = this.buildExportRow(seller)
          .map(value => `<td>${value}</td>`)
          .join("");
        tableRows += `<tr>${cells}</tr>`;
      });

      const headerRow = columns.map(col => `<th>${col.label}</th>`).join("");

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Vendedores</title>
          <style>
            @media print {
              @page { margin: 1cm; }
              body { margin: 0; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            h1 {
              text-align: center;
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .report-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            th {
              background-color: #e5e5e5;
              padding: 10px;
              text-align: center;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Relatório de Vendedores</h1>
          <div class="report-info">
            ${this.currentSelectedRegion ? `<p><strong>Região:</strong> ${this.currentSelectedRegion.name}</p>` : ""}
            ${this.currentSelectedBanca ? `<p><strong>Banca:</strong> ${this.currentSelectedBanca.name}</p>` : ""}
            <p><strong>Data de geração:</strong> ${new Date().toLocaleString("pt-BR")}</p>
          </div>
          <table>
            <thead>
              <tr>
                ${headerRow}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer">
            <p>Total de registros: ${sellers.length}</p>
          </div>
        </body>
        </html>
      `;
    },

    exportToPDF() {
      const sellers = this.filteredSellers;
      if (!sellers || sellers.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const doc = new jsPDF("landscape", "mm", "a4");

        doc.setFontSize(16);
        doc.text("Relatório de Vendedores", 14, 15);

        doc.setFontSize(10);
        let infoY = 25;
        if (this.currentSelectedRegion) {
          doc.text(`Região: ${this.currentSelectedRegion.name}`, 14, infoY);
          infoY += 5;
        }
        if (this.currentSelectedBanca) {
          doc.text(`Banca: ${this.currentSelectedBanca.name}`, 14, infoY);
          infoY += 5;
        }
        doc.text(`Data de geração: ${new Date().toLocaleString("pt-BR")}`, 14, infoY);
        infoY += 5;

        const tableColumns = this.getExportColumns().map(col => col.label);
        const tableData = sellers.map(seller => this.buildExportRow(seller));

        autoTable(doc, {
          head: [tableColumns],
          body: tableData,
          startY: infoY + 5,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
          alternateRowStyles: { fillColor: [249, 249, 249] },
          margin: { left: 14, right: 14 },
          tableWidth: "wrap"
        });

        const fileName = `Vendedores_${new Date().toISOString().slice(0, 10)}.pdf`;
        doc.save(fileName);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        toast.error("Erro ao exportar PDF");
      }
    },

    exportToExcel() {
      const sellers = this.filteredSellers;
      if (!sellers || sellers.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const headers = this.getExportColumns().map(col => col.label);
        const tableData = sellers.map(seller => this.buildExportRow(seller));

        const wb = XLSX.utils.book_new();

        const allData = [
          ["Relatório de Vendedores"],
          [""],
        ];

        if (this.currentSelectedRegion) {
          allData.push(["Região:", this.currentSelectedRegion.name]);
        }
        if (this.currentSelectedBanca) {
          allData.push(["Banca:", this.currentSelectedBanca.name]);
        }

        allData.push(
          ["Data de geração:", new Date().toLocaleString("pt-BR")],
          [""],
          headers,
          ...tableData
        );

        const ws = XLSX.utils.aoa_to_sheet(allData);

        const colWidths = headers.map(() => ({ wch: 15 }));
        ws["!cols"] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, "Vendedores");

        const fileName = `Vendedores_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        toast.error("Erro ao exportar Excel");
      }
    }
  },
};
</script>

<style scoped>
.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.card-actions .btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 1rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.card-actions .btn-action i {
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

.card-actions .btn-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.card-actions .btn-action:active {
  transform: translateY(0);
}

.btn-print {
  background-color: #6b7280;
}

.btn-print:hover {
  background-color: #4b5563;
}

.btn-pdf {
  background-color: #dc2626;
}

.btn-pdf:hover {
  background-color: #b91c1c;
}

.btn-excel {
  background-color: #16a34a;
}

.btn-excel:hover {
  background-color: #15803d;
}

@media (max-width: 768px) {
  .card-actions {
    width: 100%;
  }

  .card-actions .btn-action {
    flex: 1 1 auto;
    justify-content: center;
  }
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-zoom:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.zoom-label:hover {
  color: var(--primary-color, #3b82f6);
}

/* Estilos para múltiplas regiões inline */
.input-group.full-width {
  grid-column: 1 / -1;
}

.address-section-title .optional-badge {
  margin-left: 0.5rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: rgba(0, 0, 0, 0.06);
  color: var(--text-secondary, #666);
  vertical-align: middle;
}

[data-theme="dark"] .address-section-title .optional-badge {
  background: rgba(255, 255, 255, 0.1);
  color: #bbb;
}

.field-hint.text-danger {
  color: #c62828;
}

.cep-loading-icon {
  margin-left: 0.4rem;
  font-size: 0.85rem;
  color: var(--accent-primary, #0288d1);
}

/* Estilo para texto de ajuda do campo de região auto-preenchida */
.form-help-text {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.form-help-text i {
  color: var(--accent-primary);
  font-size: 0.875rem;
}

/* Estilo para caixa de informação sobre regiões */
.region-info-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 8px;
  color: #1565c0;
}

.region-info-box.success {
  background-color: #e8f5e8;
  border-color: #4caf50;
  color: #2e7d32;
}

.region-info-box.warning {
  background-color: #fff3e0;
  border-color: #ff9800;
  color: #f57c00;
}

.region-info-box i {
  color: #1976d2;
  font-size: 1.1rem;
  margin-top: 2px;
}

.region-info-box.success i {
  color: #4caf50;
}

.region-info-box.warning i {
  color: #ff9800;
}

.info-content {
  flex: 1;
  font-size: 0.9rem;
  line-height: 1.4;
}

[data-theme="dark"] .region-info-box {
  background-color: #0d1b2a;
  border-color: #1976d2;
  color: #90caf9;
}

[data-theme="dark"] .region-info-box.success {
  background-color: #1b2e1b;
  border-color: #4caf50;
  color: #81c784;
}

[data-theme="dark"] .region-info-box.warning {
  background-color: #2e2012;
  border-color: #ff9800;
  color: #ffb74d;
}

/* Mensagem de permissão */
.permission-info {
  padding: 7px;
  background-color: #3478f2;
  border: 1px solid #548fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  margin-bottom: 20px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  font-size: 0.9em;
  line-height: 1.4;
}

/* Aba desabilitada */
.tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}

.tab-button.disabled:hover {
  background-color: var(--bg-secondary) !important;
  color: var(--text-muted) !important;
}

/* Indicador de apenas visualização */
.no-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.875rem;
  font-style: italic;
}

.no-actions i {
  color: var(--accent-primary);
}



/* Centralização dos action-buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

/* Centralização da seller-info */
.seller-info {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Responsividade */
@media (max-width: 768px) {
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }
  .input-group {
  flex-wrap: inherit;
  }
}

/* Estilos específicos para campos de vendedor */
.field-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.field-hint i {
  color: var(--accent-primary);
  font-size: 0.875rem;
  margin-top: 2px;
}

.text-success {
  color: #4caf50;
  font-weight: 500;
}

.text-danger {
  color: #f44336;
  font-weight: 500;
}

/* Input com ícone de botão */
.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-with-icon .form-input {
  flex: 1;
}

.btn-generate-icon {
  padding: 0.6rem 1rem;
  background: var(--accent-primary);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
}

.btn-generate-icon:hover {
  background: var(--accent-hover);
  transform: translateY(-2px);
}

.btn-generate-icon i {
  font-size: 1rem;
}

/* Container de informação de região */
.region-info-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.region-info-display {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.active-region {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 2px solid #4caf50;
  border-radius: 10px;
  color: #2e7d32;
  font-weight: 500;
}

.active-region i {
  font-size: 1.25rem;
  color: #4caf50;
}

.region-name {
  font-size: 1.05rem;
  font-weight: 600;
  flex: 1;
}

.region-status {
  padding: 0.25rem 0.75rem;
  background-color: #4caf50;
  color: white;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.region-help {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-muted);
  padding-left: 0.25rem;
}

.region-help i {
  color: var(--accent-primary);
  font-size: 0.9rem;
}

[data-theme="dark"] .active-region {
  background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
  border-color: #4caf50;
  color: #a5d6a7;
}

[data-theme="dark"] .region-status {
  background-color: #388e3c;
}

/* Warning de região não selecionada */
.no-region-warning {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border: 2px solid #ff9800;
  border-radius: 10px;
}

.warning-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #e65100;
}

.warning-content i {
  font-size: 1.5rem;
  color: #ff9800;
  margin-top: 0.125rem;
}

.warning-text {
  flex: 1;
}

.warning-text strong {
  display: block;
  font-size: 1rem;
  margin-bottom: 0.25rem;
  color: #e65100;
}

.warning-text p {
  margin: 0;
  font-size: 0.9rem;
  color: #f57c00;
}

.btn-select-region {
  align-self: flex-start;
  padding: 0.6rem 1.25rem;
  background: var(--accent-primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-select-region:hover {
  background: var(--accent-hover);
  transform: translateY(-2px);
}

.btn-select-region i {
  font-size: 1rem;
}

[data-theme="dark"] .no-region-warning {
  background: linear-gradient(135deg, #e65100 0%, #f57c00 100%);
  border-color: #ff9800;
}

[data-theme="dark"] .warning-content {
  color: #ffcc80;
}

[data-theme="dark"] .warning-content i {
  color: #ffb74d;
}

[data-theme="dark"] .warning-text strong {
  color: #ffcc80;
}

[data-theme="dark"] .warning-text p {
  color: #ffb74d;
}

/* Detalhes da modalidade */
.modality-details {
  margin-top: 2rem;
  padding: 1.5rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
}

.details-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.details-title i {
  color: var(--accent-primary);
}

.details-table-container {
  overflow-x: auto;
}

.details-table {
  width: 100%;
  border-collapse: collapse;
}

.details-table thead {
  background: var(--bg-tertiary);
}

.details-table th {
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 2px solid var(--border-color);
}

.details-table td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-secondary);
}

.details-table tbody tr:last-child td {
  border-bottom: none;
}

.details-table tbody tr:hover {
  background: var(--bg-hover);
}

/* Grupo de checkbox inline */
.checkbox-group-inline {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.checkbox-input {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
  accent-color: var(--accent-primary);
}

.checkbox-text {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-primary);
}

/* Espaçamento adicional para input-group */
.input-group-spaced {
  margin-top: 1rem;
}

/* Campo com erro */
.error-field {
  border-color: #f44336 !important;
  background-color: rgba(244, 67, 54, 0.05);
}

.error-field:focus {
  border-color: #f44336 !important;
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.1);
}

/* Corrigir cores das opções do select em estado de erro */
.error-field option {
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

/* Tema claro */
:root .error-field option,
[data-theme="light"] .error-field option {
  background-color: #ffffff;
  color: #1a1a1a;
}

/* Tema escuro */
[data-theme="dark"] .error-field option {
  background-color: #1a1a1a;
  color: #e0e0e0;
}

.required-asterisk {
  color: #f44336;
  font-weight: bold;
  margin-left: 0.25rem;
}

/* Botões de ação específicos */
.btn-deactivate {
  background: #ff9800;
  color: white;
}

.btn-deactivate:hover {
  background: #f57c00;
}

.btn-activate {
  background: #4caf50;
  color: white;
}

.btn-activate:hover {
  background: #388e3c;
}

.table-count {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  white-space: nowrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  padding: 0.5rem;
  border: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition, all 0.2s);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
  border-color: var(--accent-primary);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: var(--text-primary);
  min-width: 60px;
  text-align: center;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
}

@media (max-width: 768px) {
  .pagination-footer {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>

<style>
/* Sobrescrever estilos conflitantes do vue3-toastify para evitar ícones grandes */
.Toastify__toast-container,
.Toastify__toast,
.Toastify__toast-body,
.Toastify__toast-icon {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>

